// Membership sync integrations configured from Asetukset → Jäsenyyksien synkronointi.
// The backend keeps the configs in integrations/{name}[customerid] and exposes them through the
// membershipsync-call callable; see docs/features/membership-sync.md. A venue may have several
// sources (CSV files, Tennisclub clubs) per integration.

export const CSV_SYNC = 'csvurltomembership'
export const TENNISCLUB_SYNC = 'tennisclub'

// Fields the venue admin may edit. Credentials (username/password, tcuser/tcpassword) are
// managed by Cintoia and never sent from or to the browser.
// `name` is an optional label for one source.
const CSV_FIELDS = ['name', 'url', 'separator', 'emailColumn', 'stateColumn', 'membership']
const TENNISCLUB_FIELDS = ['name', 'tcclub', 'membership']

export const DEFAULT_SEPARATOR = ','
export const MAX_SOURCES = 10
export const MAX_NAME_LENGTH = 60

// Heading of one source card: its name, or a numbered fallback ('CSV-lähde 2').
export const sourceLabel = (source, index, fallback) =>
  (source && typeof source.name === 'string' && source.name.trim()) || fallback + ' ' + (index + 1)

const nameError = (c) => (c.name && c.name.length > MAX_NAME_LENGTH ? ['Nimi saa olla enintään ' + MAX_NAME_LENGTH + ' merkkiä.'] : [])

const trimmed = (value) => (typeof value === 'string' ? value.trim() : value)

const pick = (config, fields) => {
  const out = {}
  fields.forEach((field) => {
    const value = trimmed((config || {})[field])
    if (value !== undefined && value !== null && value !== '') out[field] = value
  })
  return out
}

// Column names are matched exactly by the backend, so they are only trimmed, never re-cased.
export const pickCsvFields = (config) => {
  const out = pick(config, CSV_FIELDS)
  if (!out.separator) out.separator = DEFAULT_SEPARATOR
  return out
}

export const pickTennisclubFields = (config) => pick(config, TENNISCLUB_FIELDS)

// Returns a list of Finnish error messages; empty when the config can be saved.
export const validateCsvConfig = (config) => {
  const c = pickCsvFields(config)
  const errors = []
  if (!c.url) {
    errors.push('CSV-tiedoston osoite puuttuu.')
  } else if (!/^https?:\/\/\S+$/i.test(c.url)) {
    errors.push('Osoitteen pitää alkaa http:// tai https://.')
  }
  if (c.separator.length !== 1) errors.push('Erotinmerkin pitää olla yksi merkki.')
  if (!c.emailColumn) errors.push('Sähköpostisarakkeen nimi puuttuu.')
  if (!c.stateColumn) errors.push('Tilasarakkeen nimi puuttuu.')
  if (!c.membership) errors.push('Valitse jäsenyys, joka synkronoiduille annetaan.')
  return errors.concat(nameError(c))
}

export const validateTennisclubConfig = (config) => {
  const c = pickTennisclubFields(config)
  const errors = []
  if (!c.tcclub) errors.push('Seuran tunnus puuttuu.')
  if (!c.membership) errors.push('Valitse jäsenyys, joka synkronoiduille annetaan.')
  return errors.concat(nameError(c))
}

const isFound = (index) => typeof index === 'number' && index >= 0

// The backend (membershipsync-call) answers in English; known messages are shown in Finnish.
// validateConfig joins several messages with ', '. Unknown ones are shown as they are.
const BACKEND_MESSAGES = [
  [/^url missing$/, 'CSV-tiedoston osoite puuttuu.'],
  [/^url must start with http:\/\/ or https:\/\/$/, 'Osoitteen pitää alkaa http:// tai https://.'],
  [/^url must not contain a username or password$/, 'Osoitteessa ei saa olla käyttäjätunnusta tai salasanaa.'],
  [/^url must use a public host name$/, 'Osoitteen pitää olla julkinen verkko-osoite (ei sisäverkon tai IP-osoitetta).'],
  [/^the url redirects \(HTTP (\d+)\); use the final address$/, 'Osoite ohjaa toiseen osoitteeseen (HTTP $1). Käytä lopullista osoitetta.'],
  [/^could not get csv from url \(HTTP (\d+)\)$/, 'Tiedostoa ei saatu haettua (HTTP $1).'],
  [/^could not get csv from url(: .*)?$/, 'Tiedostoa ei saatu haettua osoitteesta.'],
  [/^separator must be one character$/, 'Erotinmerkin pitää olla yksi merkki.'],
  [/^emailColumn missing$/, 'Sähköpostisarakkeen nimi puuttuu.'],
  [/^stateColumn missing$/, 'Tilasarakkeen nimi puuttuu.'],
  [/^tcclub missing$/, 'Seuran tunnus puuttuu.'],
  [/^membership missing$/, 'Valitse jäsenyys, joka synkronoiduille annetaan.'],
  [/^membership not found$/, 'Valittua jäsenyyttä ei löydy, tai se on poistettu.'],
  [/^no email column found$/, 'Sähköpostisaraketta ei löytynyt otsikkoriviltä.'],
  [/^no state column found$/, 'Tilasaraketta ei löytynyt otsikkoriviltä.'],
  [/^not testable$/, 'Tätä synkronointia ei voi testata.'],
  [/^too many sources$/, 'Lähteitä voi olla enintään ' + MAX_SOURCES + '.'],
  [/^source not found$/, 'Lähdettä ei löytynyt. Lataa sivu uudelleen.'],
  [/^sourceId missing$/, 'Lähdettä ei löytynyt. Lataa sivu uudelleen.'],
  [/^name too long$/, 'Nimi on liian pitkä.'],
]

const translateOne = (message) => {
  const found = BACKEND_MESSAGES.find(([pattern]) => pattern.test(message))
  return found ? message.replace(found[0], found[1]) : message
}

export const translateBackendMessage = (message) => {
  if (!message) return 'tuntematon virhe'
  return String(message)
    .split(', ')
    .map((m) => translateOne(m.trim()))
    .join(' ')
}

// Turns a `test` dry-run result into Finnish lines. Each line: { severity, text }.
// hasCredentials: whether Cintoia has stored basic-auth credentials for this venue's CSV server.
export const summarizeTestResult = (result, { hasCredentials = false } = {}) => {
  if (!result) return []
  const lines = []
  if (hasCredentials && result.credentialsUsed === false) {
    lines.push({
      severity: 'warning',
      text:
        'Tallennettuja tunnuksia ei käytetty, koska osoite on eri palvelimella kuin niille asetettu. ' +
        'Jos tallennat tämän osoitteen, tunnukset poistetaan ja Cintoian pitää asettaa ne uudelleen.',
    })
  }
  if (result.error) {
    return [{ severity: 'error', text: 'Testi epäonnistui: ' + translateBackendMessage(result.message) }, ...lines]
  }
  if (result.httpStatus) {
    lines.unshift({ severity: 'success', text: 'Tiedosto haettiin (HTTP ' + result.httpStatus + ').' })
  }
  lines.push(
    isFound(result.emailColumnIndex)
      ? { severity: 'success', text: 'Sähköpostisarake löytyi.' }
      : { severity: 'error', text: 'Sähköpostisaraketta ei löytynyt otsikkoriviltä.' }
  )
  lines.push(
    isFound(result.stateColumnIndex)
      ? { severity: 'success', text: 'Tilasarake löytyi.' }
      : { severity: 'error', text: 'Tilasaraketta ei löytynyt otsikkoriviltä.' }
  )
  if (typeof result.rows === 'number') {
    lines.push({
      severity: 'info',
      text:
        'Rivejä ' + result.rows + ': aktiivisia ' + (result.activeRows || 0) +
        ', ei-aktiivisia ' + (result.inactiveRows || 0) + '.',
    })
  }
  if (result.inactiveRows) {
    lines.push({
      severity: 'info',
      text:
        'Ei-aktiivisilta riveiltä synkronoinnin antamat jäsenyydet päätetään. ' +
        'Käsin annettuihin jäsenyyksiin ei kosketa.',
    })
  }
  if (result.invalidEmails) {
    lines.push({ severity: 'warning', text: 'Virheellisiä sähköpostiosoitteita ' + result.invalidEmails + '.' })
  }
  if (typeof result.matchedUsers === 'number') {
    lines.push({
      severity: 'info',
      text:
        'Aktiivisilta riveiltä löytyi ' + result.matchedUsers +
        ' rekisteröitynyttä käyttäjää. Vain heille annetaan jäsenyys.',
    })
  }
  return lines
}
